import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MaterialConsumptionReport } from "@/components/MaterialConsumptionReport";
import { BalanceReport } from "@/components/BalanceReport";
import { DeliveryControlReport } from "@/components/DeliveryControlReport";

const statusLabels = { pendente: "Pendente", em_andamento: "Em Andamento", concluido: "Concluido", cancelado: "Cancelado" };
const priorityLabels = { baixa: "Baixa", media: "Media", alta: "Alta", urgente: "Urgente" };
const COLORS = ["hsl(222, 80%, 40%)", "hsl(45, 93%, 47%)", "hsl(160, 60%, 40%)", "hsl(0, 84%, 60%)"];

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/reports?period=${period}`, { withCredentials: true });
        setData(d);
      } catch { console.error("Erro ao carregar relatorios"); }
      finally { setLoading(false); }
    };
    fetch();
  }, [period]);

  const statusData = data ? Object.entries(data.by_status).map(([k, v]) => ({ name: statusLabels[k] || k, value: v })) : [];
  const priorityData = data ? Object.entries(data.by_priority).map(([k, v]) => ({ name: priorityLabels[k] || k, value: v })) : [];
  const clientData = data ? Object.entries(data.by_client).map(([k, v]) => ({ name: k, value: v })).sort((a, b) => b.value - a.value).slice(0, 8) : [];

  return (
    <div className="space-y-6 animate-slide-in" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Relatorios</h1>
        <p className="text-sm text-muted-foreground mt-1">Analise da producao e consumo de materiais</p>
      </div>

      <Tabs defaultValue="producao">
        <TabsList>
          <TabsTrigger value="producao" data-testid="tab-producao">Producao</TabsTrigger>
          <TabsTrigger value="consumo" data-testid="tab-consumo">Consumo de Material</TabsTrigger>
          <TabsTrigger value="saldo" data-testid="tab-saldo">Controle de Saldo</TabsTrigger>
          <TabsTrigger value="entregas" data-testid="tab-entregas">Controle de Entregas</TabsTrigger>
        </TabsList>

        <TabsContent value="consumo" className="mt-6">
          <MaterialConsumptionReport />
        </TabsContent>

        <TabsContent value="saldo" className="mt-6">
          <BalanceReport />
        </TabsContent>

        <TabsContent value="entregas" className="mt-6">
          <DeliveryControlReport />
        </TabsContent>

        <TabsContent value="producao" className="mt-6 space-y-6">
          <div className="flex items-center justify-end">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Ultima Semana</SelectItem>
                <SelectItem value="month">Ultimo Mes</SelectItem>
                <SelectItem value="year">Ultimo Ano</SelectItem>
              </SelectContent>
            </Select>
          </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Carregando...</p>
      ) : !data ? (
        <p className="text-destructive text-sm">Erro ao carregar dados</p>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border border-border shadow-none">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total de Ordens</p>
                <p className="font-heading text-3xl font-black tracking-tighter mt-1">{data.total_orders}</p>
              </CardContent>
            </Card>
            <Card className="border border-border shadow-none">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Movimentacoes</p>
                <p className="font-heading text-3xl font-black tracking-tighter mt-1">{data.total_movements}</p>
              </CardContent>
            </Card>
            <Card className="border border-border shadow-none">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Entradas</p>
                <p className="font-heading text-3xl font-black tracking-tighter mt-1 text-green-600">{data.movements_entrada}</p>
              </CardContent>
            </Card>
            <Card className="border border-border shadow-none">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Saidas</p>
                <p className="font-heading text-3xl font-black tracking-tighter mt-1 text-orange-600">{data.movements_saida}</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border border-border shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">Ordens por Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {statusData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-16">Sem dados no periodo</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border border-border shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">Ordens por Cliente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {clientData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={clientData} layout="vertical" margin={{ left: 60 }}>
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                        <Tooltip />
                        <Bar dataKey="value" fill="hsl(222, 80%, 40%)" radius={[0, 2, 2, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-16">Sem dados no periodo</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
